import { Box, Stack, Text, useMantineTheme } from '@mantine/core';
import { Bar, BarChart, CartesianGrid, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { scoreAnnualCountGoal } from '../../statusUtils';
import type { TuAswpData } from '../types';
import { latest, pctChange, previous } from '../helpers';
import { CardShell } from './CardShell';
import { MetricRow } from './MetricRow';

type TeachersCardProps = { data: TuAswpData };

export function TeachersCard({ data }: TeachersCardProps) {
  const theme = useMantineTheme();
  const series = data.teachers;
  const cur = series.length > 0 ? latest(series) : null;
  const prev = previous(series);

  const parseQuarterLabel = (label: string): { q: number; y: number } => {
    // Expected format: "Q1 2026"
    const q = Number(label.slice(1, 2));
    const y = Number(label.split(' ')[1]);
    return { q: Number.isFinite(q) ? q : 1, y: Number.isFinite(y) ? y : new Date().getFullYear() };
  };

  const curQ = parseQuarterLabel(cur?.quarter ?? '');
  const trainedYtd = series.reduce((acc, p) => {
    const qp = parseQuarterLabel(p.quarter);
    return acc + (qp.y === curQ.y && qp.q <= curQ.q ? p.teachersTrained : 0);
  }, 0);
  const trainedYtdPrevYear = series.reduce((acc, p) => {
    const qp = parseQuarterLabel(p.quarter);
    return acc + (qp.y === curQ.y - 1 && qp.q <= curQ.q ? p.teachersTrained : 0);
  }, 0);
  const qoq = cur ? pctChange(cur.teachersTrained, prev?.teachersTrained ?? null) : null;
  const yoy = trainedYtdPrevYear > 0 ? ((trainedYtd - trainedYtdPrevYear) / trainedYtdPrevYear) * 100 : null;
  const arrow = (n: number): '↑' | '↓' | '' => (n > 0 ? '↑' : n < 0 ? '↓' : '');
  const qoqText = qoq === null ? '—' : arrow(qoq) ? `${arrow(qoq)}${Math.abs(qoq).toFixed(2)}% QoQ` : '0.00% QoQ';
  const yoyText =
    yoy === null ? '— YoY' : arrow(yoy) ? `${arrow(yoy)}${Math.abs(yoy).toFixed(2)}% YoY` : '0.00% YoY';

  const scored = scoreAnnualCountGoal({
    ytd: trainedYtd,
    goals: data.goalsByMetric['tuAswp.teachersTrained'],
    field: 'teachersTrained',
    year: curQ.y,
    quarterLabel: cur?.quarter ?? '',
  });
  const goalPerYear = scored.goal;
  const status = scored;

  const annualTotals = series.reduce<Record<number, number>>((acc, point) => {
    const qp = parseQuarterLabel(point.quarter);
    acc[qp.y] = (acc[qp.y] ?? 0) + point.teachersTrained;
    return acc;
  }, {});

  const years = Object.keys(annualTotals)
    .map((year) => Number(year))
    .filter((year) => Number.isFinite(year));
  const minYear = years.length > 0 ? Math.min(...years) : curQ.y;
  const maxYear = years.length > 0 ? Math.max(...years) : curQ.y;

  const annualData = Array.from({ length: maxYear - minYear + 1 }, (_, idx) => minYear + idx).map((year) => ({
    year,
    yearLabel: String(year),
    total: annualTotals[year] ?? 0,
  }));

  const tooltipShellStyle = {
    backgroundColor: theme.colorScheme === 'dark' ? '#000' : '#fff',
    border: `1px solid ${theme.colorScheme === 'dark' ? '#333' : '#ddd'}`,
    borderRadius: 10,
    color: theme.colorScheme === 'dark' ? '#fff' : '#000',
    padding: '10px 12px',
    maxWidth: 320,
    whiteSpace: 'normal' as const,
    wordBreak: 'break-word' as const,
  } as const;

  return (
    <CardShell title="Teachers Trained" subtitle={cur?.quarter ?? 'No data'} statusLabel={status.label} statusColor={status.color}>
      <Stack spacing="sm">
        <MetricRow
          items={[
            {
              label: 'This Quarter',
              labelTooltip: 'Unique teachers trained this quarter.',
              value: (
                <Text size="lg" weight={800}>
                  {cur?.teachersTrained ?? '—'}
                </Text>
              ),
              sublabel: qoqText,
            },
            {
              label: 'Trained (YTD)',
              labelTooltip:
                goalPerYear != null
                  ? `Unique teachers trained calendar year to date. Goal: ${goalPerYear}/year.`
                  : 'Unique teachers trained calendar year to date.',
              value: (
                <Text size="lg" weight={800}>
                  {trainedYtd}
                </Text>
              ),
              sublabel: (
                <Stack spacing={0}>
                  <Text size="xs" color="dimmed">
                    {yoyText}
                  </Text>
                  {goalPerYear != null ? (
                    <Text size="xs" color="dimmed">
                      Goal {goalPerYear}/year
                    </Text>
                  ) : null}
                </Stack>
              ),
            },
          ]}
        />

        <Text size="xs" color="dimmed" weight={700} sx={{ letterSpacing: 0.2, marginTop: '0.75rem' }}>
          Annual Teachers Trained
        </Text>

        <Box sx={{ height: 170 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={annualData}>
              <CartesianGrid strokeDasharray="3 3" opacity={0.18} vertical={false} />
              <XAxis dataKey="yearLabel" tick={{ fontSize: 11 }} axisLine={false} tickLine={false} interval="preserveStartEnd" minTickGap={12} />
              <YAxis orientation="right" tick={{ fontSize: 11 }} allowDecimals={false} axisLine={false} tickLine={false} width={44} tickMargin={8} />
              {goalPerYear != null ? (
                <ReferenceLine y={goalPerYear} stroke="#fa5252" strokeDasharray="4 4" ifOverflow="extendDomain" />
              ) : null}
              <Tooltip
                content={({ active, payload, label }) => {
                  if (!active || !payload || payload.length === 0) return null;
                  const v = payload[0]?.value;
                  return (
                    <div style={tooltipShellStyle as any}>
                      <div style={{ fontWeight: 700, marginBottom: 6, fontSize: 12, opacity: 0.9 }}>{label}</div>
                      <div style={{ fontWeight: 900, fontSize: 14, lineHeight: 1.2 }}>
                        {typeof v === 'number' ? v : '—'} Teachers
                      </div>
                    </div>
                  );
                }}
              />
              <Bar
                dataKey="total"
                name="Trained"
                fill={theme.colors.blue[6]}
                  barSize={18}
                radius={[6, 6, 0, 0]}
                isAnimationActive={false}
              />
            </BarChart>
          </ResponsiveContainer>
        </Box>
      </Stack>
    </CardShell>
  );
}
